/**
 * Chat History API Service
 *
 * Handles communication with the backend for chat session and message persistence.
 * Automatically attaches Firebase auth tokens when available.
 */

import { API_BASE, getAuthHeaders, fetchApi } from './apiClient';

export interface ChatSession {
  id: string;
  user_id: string | null;
  title: string;
  persona: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface ChatMessage {
  id: string;
  session_id: string;
  role: 'user' | 'assistant';
  content: string;
  confidence: number | null;
  confidence_level: string | null;
  sources: Array<{
    title: string;
    section: string;
    path: string;
    relevance_score: number;
  }> | null;
  query_type: string | null;
  feedback: 'positive' | 'negative' | null;
  created_at: string;
}

export interface ChatSessionWithMessages {
  session: ChatSession;
  messages: ChatMessage[];
}

class ChatHistoryApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE}/history`;
  }

  /**
   * Create a new chat session
   */
  async createSession(params: {
    user_id?: string;
    title?: string;
    persona?: string;
  } = {}): Promise<ChatSession> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/sessions`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        user_id: params.user_id || null,
        title: params.title || 'New Chat',
        persona: params.persona || 'client',
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create session: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get all chat sessions
   */
  async getSessions(params: {
    user_id?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<ChatSession[]> {
    const searchParams = new URLSearchParams();
    if (params.user_id) searchParams.set('user_id', params.user_id);
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.offset) searchParams.set('offset', params.offset.toString());

    const url = `${this.baseUrl}/sessions${searchParams.toString() ? `?${searchParams}` : ''}`;
    const headers = await getAuthHeaders();
    const response = await fetchApi(url, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get sessions: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get a single chat session with all its messages
   */
  async getSession(sessionId: string): Promise<ChatSessionWithMessages> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/sessions/${sessionId}`, { headers });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Session not found');
      }
      throw new Error(`Failed to get session: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update a chat session
   */
  async updateSession(
    sessionId: string,
    updates: { title?: string; persona?: string }
  ): Promise<ChatSession> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/sessions/${sessionId}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`Failed to update session: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete a chat session
   */
  async deleteSession(sessionId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/sessions/${sessionId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete session: ${response.statusText}`);
    }
  }

  /**
   * Clear all chat sessions
   */
  async clearAllSessions(userId?: string): Promise<{ sessions_deleted: number }> {
    const searchParams = new URLSearchParams();
    if (userId) searchParams.set('user_id', userId);

    const url = `${this.baseUrl}/sessions${searchParams.toString() ? `?${searchParams}` : ''}`;
    const headers = await getAuthHeaders();
    const response = await fetchApi(url, { method: 'DELETE', headers });

    if (!response.ok) {
      throw new Error(`Failed to clear sessions: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Add a message to a chat session
   */
  async addMessage(
    sessionId: string,
    message: {
      role: 'user' | 'assistant';
      content: string;
      confidence?: number;
      confidence_level?: string;
      sources?: Array<{
        title: string;
        section: string;
        path: string;
        relevance_score: number;
      }>;
      query_type?: string;
    }
  ): Promise<ChatMessage> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/sessions/${sessionId}/messages`, {
      method: 'POST',
      headers,
      body: JSON.stringify(message),
    });

    if (!response.ok) {
      throw new Error(`Failed to add message: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update message feedback
   */
  async updateMessageFeedback(
    messageId: string,
    feedback: 'positive' | 'negative' | 'need_more_detail' | null
  ): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/messages/${messageId}/feedback`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ feedback }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update feedback: ${response.statusText}`);
    }
  }
}

export const chatHistoryApi = new ChatHistoryApi();
